import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useState, useCallback, useMemo, useRef, useEffect, type ReactNode, type CSSProperties } from "react";
import { AlertCircle, Bot, ChevronDown, ChevronRight, ChevronUp, Flag, Info, CircleDashedCheck, List, Loader2, MessageCircle, MessageSquare, Phone, RefreshCw, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { cn, toUkIsoString } from "@/lib/utils";
import { formatBritishDateTime, formatTimeAgo, isClaimStale } from "@/helpers/dateUtils";
import { AddOn, VehicleDetails, FinanceFigures, FinanceFiguresInput, QuoteSnapshotPayload, UnderwritingActivity, UnderwritingNavOrigin, RequestOverview, NoteModel, QuoteLenderRef, ActionDecision, TranscriptChannel, TranscriptEntry, TranscriptSegment, RequestStatus } from "@/models/underwriting";
import { ClaimConfirmDialog } from "@/components/UnderwritingDashboard/ClaimConfirmDialog";
import { ActionRequestDialog } from "@/components/UnderwritingDashboard/ActionRequestDialog";
import { QuoteDetailsDialog } from "@/components/UnderwritingDashboard/QuoteDetailsDialog";
import { AddVehicleModal } from "@/components/UnderwritingDashboard/AddVehicleModal";
import { ChangeFinanceModal } from "@/components/UnderwritingDashboard/ChangeFinanceModal";
import { ChangeExtrasModal, SelectedExtra } from "@/components/UnderwritingDashboard/ChangeExtrasModal";
import { FormattedHtmlText } from "@/components/UnderwritingDashboard/FormattedHtmlText";
import { MessagesTab } from "@/components/UnderwritingDashboard/MessagesTab";
import { VehicleDetailModel, VehicleExtraFilterParams } from "@/models/vehicle";
import { quoteSnapshotService } from "@/services/QuoteSnapshotService";
import { underwritingService } from "@/services/UnderwritingService";
import { proposalService } from "@/services/ProposalService";
import { useAppData } from "@/context/AppDataContext";
import { useApi, type UseApiResult } from "@/hooks/useApi";
import { mapMonthlyPriceToQuoteRequest, buildLoanProposalRequest } from "@/helpers/quoteProposal";
import { formatUnderwritingQuoteAmount, isRequestActionable } from "@/helpers/underwritingDashboard";
import { compareQuotes, type QuoteSortBy } from "@/helpers/quoteSort";
import { getDisplayStatus, isQuoteProcessing, isQuoteProvisionalAcceptance } from "@/helpers/quoteStatus";
import {
  mapProposals,
  mapSnapshotCustomer,
  mapSnapshotDealer,
  mapSnapshotVehicle,
} from "@/helpers/pricingMappers";
import { toast } from "sonner";
import { calculate } from "@cf247/pricing/dist/calculator/calculator";
import { FinanceType, FinanceCompany, AncillaryProductLenderStanceType } from "@cf247/pricing";
import type { MonthlyPrice, Vehicle, Dealer } from "@cf247/pricing";

function formatCurrency(n: number | undefined | null): string {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 2 }).format(n);
}

function vehicleDetailsFromSnapshot(v: Vehicle | undefined): VehicleDetails {
  return {
    makeModel: v ? `${v.make} ${v.model}${v.trim ? " " + v.trim : ""}` : "—",
    registration: v?.vrm ?? "—",
    colour: v?.colour ?? "—",
    mileage: v?.mileage != null ? `${v.mileage.toLocaleString()} mi` : "—",
    cashPrice: formatCurrency(v?.price),
  };
}

/** Maps a VRM-search result onto the Summary tab's vehicle fields. */
function vehicleDetailsFromModel(v: VehicleDetailModel): VehicleDetails {
  return {
    makeModel: [v.make, v.model, v.trim].filter(Boolean).join(" ") || "—",
    registration: v.regNumber || "—",
    colour: v.colour || "—",
    mileage: v.mileage != null ? `${v.mileage.toLocaleString()} mi` : "—",
    cashPrice: formatCurrency(v.price),
  };
}

/** Formats the editable finance inputs back into the display figures. */
function financeFiguresFromInput(f: FinanceFiguresInput): FinanceFigures {
  return {
    cashPrice: formatCurrency(f.cashPrice),
    deposit: formatCurrency(f.deposit),
    partExchange: formatCurrency(f.partExchange),
    settlement: formatCurrency(f.settlement),
    annualMileage: `${f.annualMileage.toLocaleString()} mi/yr`,
    existingFinanceCo: null,
    loanAmount: formatCurrency(f.loanAmount),
    term: `${f.term} months`,
  };
}

function financeFiguresFromSnapshot(
  v: Vehicle | undefined,
  deposit: number,
  pxValue: number,
  pxSettlement: number,
  annualMileage: number,
  maxTerm: number,
  dealer: Dealer | undefined
): FinanceFigures {
  // Mirror the pricing library's balanceToFinance: the dealer admin fee is added
  // to the loan when it's set to be included in finance. Finance-stance extras are
  // not represented here — the snapshot stores only product ids/stances (no price),
  // so their finance value is resolved per-lender when quotes are generated.
  const includedAdminFee =
    dealer?.adminFee?.includedInFinance ? dealer.adminFee.amount ?? 0 : 0;
  const loanAmount =
    (v?.price ?? 0) + includedAdminFee - deposit - pxValue + pxSettlement;
  return {
    cashPrice: formatCurrency(v?.price),
    deposit: formatCurrency(deposit),
    partExchange: formatCurrency(pxValue),
    settlement: formatCurrency(pxSettlement),
    annualMileage: `${annualMileage.toLocaleString()} mi/yr`,
    existingFinanceCo: null,
    loanAmount: formatCurrency(loanAmount > 0 ? loanAmount : 0),
    term: `${maxTerm} months`,
  };
}

// Helpers

/**
 * Builds the Add-ons list from the snapshot's included extras. Product names and
 * prices are only exposed on the generated quotes' `extrasPrices`, so they resolve
 * once quotes have been generated; before then the product id is shown as a fallback.
 */
function addOnsFromSnapshot(
  included: { productId: number; stance: AncillaryProductLenderStanceType }[] | null | undefined,
  quotes: MonthlyPrice[] | null
): AddOn[] {
  if (!included?.length) return [];
  const lookup = new Map<number, { name: string; price: number }>();
  for (const quote of quotes ?? []) {
    for (const extra of quote.extrasPrices ?? []) {
      if (!lookup.has(extra.productId)) {
        lookup.set(extra.productId, { name: extra.productName, price: extra.productPrice });
      }
    }
  }
  const addOns: AddOn[] = [];
  for (const inc of included) {
    const info = lookup.get(inc.productId);
    // Product name/price only exist on generated quotes; skip until they resolve.
    if (!info) continue;
    addOns.push({
      name: info.name,
      price: formatCurrency(info.price),
      status: inc.stance === AncillaryProductLenderStanceType.Finance ? "In Finance" : "Not Included",
    });
  }
  return addOns;
}

/** Turns a FinanceCompany code into a readable name (e.g. CloseBrothers → "Close Brothers"). */
function lenderDisplayName(code: number | null | undefined): string | null {
  if (code == null) return null;
  const key = FinanceCompany[code as FinanceCompany];
  if (!key) return null;
  return key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");
}

const LEAVE_WITHOUT_ACTION_MESSAGE =
  "You've proposed a quote to a lender but haven't recorded a decision. Are you sure you want to leave without submitting an action?";

/** Maximum time an application can stay open without an action before the user is sent back. */
const INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000;

// Sub-components

function Breadcrumb({
  customerName,
  originLabel,
  onDashboardClick,
}: {
  customerName: string;
  originLabel: string;
  onDashboardClick: () => void;
}) {
  return (
    <nav className="flex items-center gap-1 text-sm text-muted-foreground">
      <button
        onClick={onDashboardClick}
        className="hover:text-foreground transition-colors"
      >
        {originLabel}
      </button>
      <ChevronRight className="size-3.5 shrink-0" />
      <span className="font-medium text-foreground">{customerName}</span>
    </nav>
  );
}

/** A plain titled card used for the Overview and Quotes panels on the left column. */
function SectionCard({
  title,
  action,
  children,
  className,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("rounded-xl border border-border bg-card shadow-sm", className)}>
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h2 className="text-[12px] font-semibold text-foreground">{title}</h2>
        {action}
      </div>
      {children}
    </div>
  );
}

const STATUS_STYLES: Record<string, { label: string; className: string }> = {
  Pending: { label: "Pending", className: "text-[11px] border-slate-200 bg-slate-100 text-slate-600" },
  InReview: { label: "In Progress", className: "text-[11px] border-amber-200 bg-amber-100 text-slate-700" },
  Approved: { label: "Approved", className: "text-[11px] border-emerald-200 bg-emerald-100 text-slate-700" },
  Declined: { label: "Declined", className: "text-[11px] border-rose-200 bg-rose-100 text-slate-700" },
  Alternative: { label: "Alternative", className: "text-[11px] border-blue-200 bg-blue-100 text-slate-700" },
  MoreInfo: { label: "More Info", className: "text-[11px] border-violet-200 bg-violet-100 text-slate-700" },
  Cancelled: { label: "Cancelled", className: "text-[11px] border-slate-200 bg-slate-100 text-slate-500" },
};

function StatusPill({ status }: { status: string }) {
  const style = STATUS_STYLES[status] ?? STATUS_STYLES.Pending;
  return (
    <span className={cn("inline-flex rounded-full border px-4 py-0.5 text-xs font-medium", style.className)}>
      {style.label}
    </span>
  );
}

function TypeBadge({ type }: { type: string }) {
  return (
    <span className="inline-flex rounded-full border border-slate-200 px-4 py-0.5 font-medium text-[11px] text-slate-700">
      {type}
    </span>
  );
}

function BusinessBadge({ channel }: { channel: string }) {
  const label = channel === "Consumer Direct" ? "Consumer" : "Dealer";
  return (
    <span className="inline-flex rounded-full border border-slate-200 px-4 py-0.5 font-medium text-[11px] text-slate-700">
      {label}
    </span>
  );
}

/** Header claim control — replaces the standalone availability banner. */
function HeaderClaimButton({
  lockedBy,
  currentUserName,
  status,
  claimedOn,
  onClaim,
  onUnclaim,
}: {
  status: RequestStatus;
  lockedBy: string | null;
  currentUserName: string | null;
  claimedOn: string | null;
  onClaim?: () => Promise<void>;
  onUnclaim?: () => Promise<void>;
}) {
  const isMine = lockedBy !== null && lockedBy === currentUserName;
  const isOther = lockedBy !== null && !isMine;
  const isStale = claimedOn !== null && isClaimStale(claimedOn);
  const isActionable = isRequestActionable(status);

  if (isMine) {
    if (!isActionable) return null;
    return (
      <ClaimConfirmDialog
        trigger={
          <Button variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-100 text-xs px-2.5 py-1.5 h-auto">
            Unclaim
          </Button>
        }
        title="Release Claim"
        description="This will release the request back to the queue for anyone to claim. Continue?"
        confirmLabel="Unclaim"
        actionName="unclaim"
        failureMessage="Failed to release the claim. Please try again."
        onConfirm={onUnclaim}
      />
    );
  }

  if (isOther) {
    if (!isStale || !isActionable) return null;
    return (
      <ClaimConfirmDialog
        trigger={
          <Button variant="outline" className="border-amber-400 text-slate-700 hover:bg-amber-100 text-xs px-2.5 py-1.5 h-auto">
            Claim Over
          </Button>
        }
        title="Override Claim"
        description={`This request has been held by ${lockedBy} for over 15 minutes. Do you want to take over and assign it to yourself?`}
        confirmLabel="Take Over"
        onConfirm={onClaim}
      />
    );
  }

  return (
    <ClaimConfirmDialog
      trigger={<Button variant="outline" className="text-xs px-2.5 py-2 h-auto">Claim</Button>}
      onConfirm={onClaim}
    />
  );
}

// Application Notes

function NoteItem({ note, separated }: { note: NoteModel; separated?: boolean }) {
  const date = new Date(note.commentDate).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const name = note.userName ?? "System";

  return (
    <div className={cn("flex flex-col gap-1", separated && "border-t border-border pt-3")}>
      <FormattedHtmlText
        as="p"
        html={note.comment}
        className="min-w-0 [overflow-wrap:anywhere] text-[11px] leading-snug text-foreground [&_a]:underline [&_a]:underline-offset-2"
      />
      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
        {note.highImportance && <Flag className="size-3 shrink-0 text-amber-500" />}
        <span className="font-medium text-foreground/80">{name}</span>
        <span>·</span>
        <span>{date}</span>
        {note.department && <span>({note.department})</span>}
      </div>
    </div>
  );
}

function NotesTab({
  applicationId,
  currentUserName,
  currentUserId,
  onNoteAdded,
}: {
  applicationId: number | null;
  currentUserName: string | null;
  currentUserId: number | null;
  onNoteAdded: () => void;
}) {
  const notesResult = useApi(
    (signal) => underwritingService.getNotes(applicationId!, signal),
    [applicationId],
    { skip: applicationId == null }
  );

  const importantNotes = useMemo(
    () =>
      (notesResult.data ?? [])
        .filter((n) => n.highImportance)
        .sort((a, b) => (a.commentDate < b.commentDate ? 1 : -1)),
    [notesResult.data]
  );
  const otherNotes = useMemo(
    () =>
      (notesResult.data ?? [])
        .filter((n) => !n.highImportance)
        .sort((a, b) => (a.commentDate < b.commentDate ? 1 : -1)),
    [notesResult.data]
  );

  const [comment, setComment] = useState("");
  const [isImportant, setIsImportant] = useState(false);
  const [sendToDealer, setSendToDealer] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const listTopRef = useRef<HTMLDivElement>(null);

  // Scroll to top of list when notes refresh after adding
  useEffect(() => {
    if (!notesResult.loading && notesResult.data) {
      listTopRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [notesResult.data, notesResult.loading]);

  const handleAddNote = async () => {
    if (!comment.trim() || applicationId == null) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await underwritingService.addNote({
        applicationId,
        comment: comment.trim(),
        createdBy: currentUserName ?? "",
        isImportant,
        isPublic: sendToDealer,
        isUserComment: true,
        userId: currentUserId ?? 0,
      });
      setComment("");
      setIsImportant(false);
      setSendToDealer(false);
      notesResult.refetch();
      onNoteAdded();
    } catch {
      setSubmitError("Failed to add note. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex h-full flex-col">
      {/* Scrollable notes list */}
      <div className="flex-1 overflow-y-auto min-h-0">
        <div ref={listTopRef} />

        {notesResult.loading && (
          <div className="flex items-center gap-2 px-4 py-4 text-[11px] text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            Loading notes…
          </div>
        )}

        {notesResult.error && (
          <p className="px-4 py-3 text-[11px] text-rose-600">{notesResult.error}</p>
        )}

        {!notesResult.loading && !notesResult.error && (
          <>
            {importantNotes.length > 0 && (
              <div className="bg-amber-50 px-4 py-3">
                <p className="mb-2 text-[12px] font-semibold text-foreground">Important Notes</p>
                <div className="flex flex-col gap-3">
                  {importantNotes.map((note, i) => <NoteItem key={i} note={note} separated={i > 0} />)}
                </div>
              </div>
            )}

            <div className="px-4 py-3">
              <p className="mb-2 text-[12px] font-semibold text-foreground">All Notes</p>
              {otherNotes.length > 0 ? (
                <div className="flex flex-col gap-3">
                  {otherNotes.map((note, i) => <NoteItem key={i} note={note} separated={i > 0} />)}
                </div>
              ) : (
                !importantNotes.length && (
                  <p className="py-4 text-[11px] text-muted-foreground">No note(s) available</p>
                )
              )}
            </div>
          </>
        )}
      </div>

      {/* Add note */}
      <div className="shrink-0 border-t border-border p-3">
        <div className="rounded-lg border border-border focus-within:ring-2 focus-within:ring-slate-600">
          <textarea
            className="w-full resize-none rounded-t-lg bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
            rows={3}
            placeholder="Add a note…"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          <div className="flex items-end justify-between gap-3 px-2 py-2">
            <div className="flex flex-col items-start gap-2">
              <label className="flex cursor-pointer items-center gap-2 text-xs text-foreground">
                <Checkbox
                  checked={isImportant}
                  onCheckedChange={(value) => setIsImportant(value === true)}
                  disabled={submitting}
                />
                Important
              </label>
              <label className="flex cursor-pointer items-center gap-2 text-xs text-foreground">
                <Checkbox
                  checked={sendToDealer}
                  onCheckedChange={(value) => setSendToDealer(value === true)}
                  disabled={submitting}
                />
                Send to dealer
              </label>
            </div>
            <Button
              size="sm"
              className="bg-purple-600 hover:bg-purple-700 text-white"
              onClick={handleAddNote}
              disabled={submitting || !comment.trim()}
            >
              {submitting ? <Loader2 className="size-3.5 animate-spin mr-1" /> : null}
              Add note
            </Button>
          </div>
        </div>
        {submitError && <p className="mt-1.5 text-xs text-rose-600">{submitError}</p>}
      </div>
    </div>
  );
}

// Application History

/** "14 Jul, 15:02" — day, short month, then 24h time. */
function formatHistoryDate(isoString: string): string {
  const d = new Date(isoString);
  const date = d.toLocaleString("en-GB", { day: "2-digit", month: "short" });
  const time = d.toLocaleString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${date}, ${time}`;
}

function HistoryTab({ historyResult }: { historyResult: UseApiResult<NoteModel[]> }) {
  // Newest first, so the timeline reads top-to-bottom in chronological order.
  const entries = useMemo(
    () =>
      (historyResult.data ?? [])
        .slice()
        .sort((a, b) => (a.commentDate < b.commentDate ? 1 : -1)),
    [historyResult.data]
  );

  return (
    <div className="flex h-full flex-col">
      <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto px-4 py-4">
        {historyResult.loading && (
          <div className="flex items-center gap-2 py-4 text-[11px] text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            Loading history…
          </div>
        )}

        {historyResult.error && (
          <p className="py-3 text-[11px] text-rose-600">{historyResult.error}</p>
        )}

        {!historyResult.loading && !historyResult.error && (
          entries.length > 0 ? (
            <ol className="flex flex-col">
              {entries.map((entry, i) => (
                <li key={i} className="relative flex gap-3 pb-5 last:pb-0">
                  {i < entries.length - 1 && (
                    <span className="absolute left-[5px] top-3 h-full w-px bg-border" aria-hidden />
                  )}
                  <span className="mt-1 size-2.5 shrink-0 rounded-full border-2 border-slate-300 bg-background" />
                  <div className="flex min-w-0 flex-col gap-0.5 [overflow-wrap:anywhere]">
                    <FormattedHtmlText
                      as="p"
                      html={entry.comment}
                      className="text-[12px] leading-snug text-foreground [&_a]:underline [&_a]:underline-offset-2"
                    />
                    <span className="text-[11px] text-muted-foreground">
                      {formatHistoryDate(entry.commentDate)}
                    </span>
                  </div>
                </li>
              ))}
            </ol>
          ) : (
            <p className="py-4 text-[11px] text-muted-foreground">No history available</p>
          )
        )}
      </div>
    </div>
  );
}

// Transcripts

/** "15 Jul 2026, 14:02" — day, short month, year, then 24h time. */
function formatTranscriptDate(isoString: string): string {
  const d = new Date(isoString);
  const date = d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  const time = d.toLocaleString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${date}, ${time}`;
}

const TRANSCRIPT_ICONS: Record<TranscriptChannel, typeof Phone> = {
  Call: Phone,
  WhatsApp: MessageCircle,
  SMS: MessageSquare,
  ChatBot: Bot,
};

/** AI/agent turns render as the "sent" side of the conversation; caller/human as "received". */
function isAgentSpeaker(speaker: string): boolean {
  const value = speaker.toLowerCase();
  return value.includes("agent") || value.includes("ai");
}

function TranscriptSegments({ segments }: { segments: TranscriptSegment[] }) {
  if (segments.length === 0) {
    return <p className="text-[11px] text-muted-foreground">No transcript available.</p>;
  }
  return (
    <div className="flex flex-col gap-2">
      {segments.map((seg, i) => {
        const isAgent = isAgentSpeaker(seg.speaker);
        return (
          <div key={i} className={cn("flex flex-col gap-0.5", isAgent ? "items-end" : "items-start")}>
            <span className="px-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              {seg.speaker}
            </span>
            <FormattedHtmlText
              as="div"
              html={seg.content}
              className={cn(
                "max-w-[85%] rounded-2xl px-3 py-2 text-[12px] leading-snug [&_a]:underline [&_a]:underline-offset-2",
                isAgent
                  ? "rounded-tr-sm bg-[#f4f1ff] text-foreground [&_a]:text-violet-700"
                  : "rounded-tl-sm bg-muted text-foreground"
              )}
            />
          </div>
        );
      })}
    </div>
  );
}

function TranscriptRow({ entry }: { entry: TranscriptEntry }) {
  const [open, setOpen] = useState(false);
  const [segments, setSegments] = useState<TranscriptSegment[] | null>(entry.segments);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const Icon = TRANSCRIPT_ICONS[entry.channel];

  const handleToggle = async () => {
    const next = !open;
    setOpen(next);
    // Call transcripts are fetched lazily the first time a row is expanded.
    if (next && segments == null && entry.recordingId) {
      setLoading(true);
      setError(null);
      try {
        const result = await underwritingService.getCallTranscript(entry.recordingId);
        setSegments(result);
      } catch {
        setError("Failed to load transcript. Please try again.");
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="border-b border-border last:border-b-0">
      <button
        type="button"
        onClick={handleToggle}
        aria-expanded={open}
        className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50"
      >
        <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <div className="flex flex-wrap items-center gap-x-2">
            <span className="text-[13px] font-semibold text-foreground">{entry.title}</span>
            <span className="text-[11px] text-muted-foreground">{formatTranscriptDate(entry.date)}</span>
          </div>
          {entry.summary && !open && (
            <FormattedHtmlText
              as="div"
              html={entry.summary}
              className="line-clamp-4 text-[12px] leading-snug text-muted-foreground [&_a]:underline [&_a]:underline-offset-2"
            />
          )}
        </div>
        <ChevronRight
          className={cn(
            "mt-0.5 size-4 shrink-0 text-muted-foreground transition-transform",
            open && "rotate-90"
          )}
        />
      </button>

      {open && (
        <div className="px-4 pb-4 pl-11">
          {entry.summary && (
            <FormattedHtmlText
              as="div"
              html={entry.summary}
              className="mb-2 text-[12px] leading-snug text-muted-foreground [&_a]:underline [&_a]:underline-offset-2"
            />
          )}
          {entry.summary && (loading || error || segments) && (
            <div className="my-2 flex items-center gap-2" role="separator">
              <span className="h-px flex-1 bg-border" />
              <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                Conversation
              </span>
              <span className="h-px flex-1 bg-border" />
            </div>
          )}
          {loading && (
            <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              Loading transcript…
            </div>
          )}
          {error && <p className="text-[11px] text-rose-600">{error}</p>}
          {!loading && !error && segments && <TranscriptSegments segments={segments} />}
        </div>
      )}
    </div>
  );
}

function TranscriptsTab({ applicationId }: { applicationId: number | null }) {
  const transcriptsResult = useApi(
    (signal) => underwritingService.getTranscripts(applicationId!, signal),
    [applicationId],
    { skip: applicationId == null }
  );

  const entries = transcriptsResult.data ?? [];

  return (
    <div className="flex h-full flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto">
        {transcriptsResult.loading && (
          <div className="flex items-center gap-2 px-4 py-4 text-[11px] text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            Loading transcripts…
          </div>
        )}

        {transcriptsResult.error && (
          <p className="px-4 py-3 text-[11px] text-rose-600">{transcriptsResult.error}</p>
        )}

        {!transcriptsResult.loading && !transcriptsResult.error && (
          entries.length > 0 ? (
            entries.map((entry) => <TranscriptRow key={entry.id} entry={entry} />)
          ) : (
            <p className="px-4 py-4 text-[11px] text-muted-foreground">No transcripts available</p>
          )
        )}
      </div>
    </div>
  );
}

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid min-h-[46px] grid-cols-[132px_1fr] items-center gap-3 px-4 py-2">
      <span className="text-[11px] text-muted-foreground">{label}</span>
      <div className="text-[11px] text-foreground">{children}</div>
    </div>
  );
}

/** The two-column Overview card content on the left column. */
function OverviewContent({
  activity,
  overview,
  lockedBy,
}: {
  activity: UnderwritingActivity;
  overview: RequestOverview | null;
  lockedBy: string | null;
}) {
  const submitted = formatBritishDateTime(activity.createdOn, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 md:divide-x divide-border">
      <div className="divide-y divide-border">
        <DetailRow label="Reference">
          <span>{activity.applicationId}</span>
        </DetailRow>
        <DetailRow label="Submitted">{submitted}</DetailRow>
        <DetailRow label="Submitted by">{overview?.submittedBy || "—"}</DetailRow>
        <DetailRow label="Claimed by">{lockedBy || "—"}</DetailRow>
        <DetailRow label="Status">
          <StatusPill status={activity.status} />
        </DetailRow>
        <DetailRow label="Business">
          {activity.channel ? <BusinessBadge channel={activity.channel} /> : "—"}
        </DetailRow>
        <DetailRow label="Affiliate">{activity.affiliate || "—"}</DetailRow>
      </div>
      <div className="divide-y divide-border">
        <DetailRow label="Resubmissions">{activity.resubmissionCount ?? 0}</DetailRow>
        <DetailRow label="Type">
          <TypeBadge type={activity.requestType} />
        </DetailRow>
        <DetailRow label="Panel #1 Lender">{overview?.panelLenderName || "—"}</DetailRow>
        <DetailRow label="Requested Lender">{overview?.requestedLenderName || "—"}</DetailRow>
        <DetailRow label="Reason">{overview?.subject || "—"}</DetailRow>
        <DetailRow label="Notes">
          <span className="leading-relaxed">{overview?.reason || "—"}</span>
        </DetailRow>
      </div>
    </div>
  );
}

function SummaryField({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-border py-2 last:border-0">
      <span className="text-[11px] text-muted-foreground">{label}</span>
      <span className="text-right text-[11px] text-foreground">{value}</span>
    </div>
  );
}

/** The Summary tab content on the right column: Vehicle, Finance and Fees/Add-ons. */
function SummaryTab({
  vehicle,
  finance,
  addOns,
  addOnsPending,
  addOnsLoading,
  hasVehicle,
  onEditVehicle,
  onEditFinance,
  onEditExtras,
}: {
  vehicle: VehicleDetails;
  finance: FinanceFigures;
  addOns: AddOn[];
  addOnsPending: boolean;
  addOnsLoading: boolean;
  hasVehicle: boolean;
  onEditVehicle?: () => void;
  onEditFinance?: () => void;
  onEditExtras?: () => void;
}) {
  return (
    <div className="flex flex-col gap-6 px-4 py-4">
      <section>
        <div className="mb-1 flex items-center justify-between">
          <h3 className="text-[12px] font-semibold text-foreground">Vehicle</h3>
          {onEditVehicle && (
            <Button
              variant="outline"
              size="sm"
              className="h-6 px-2 text-[11px]"
              onClick={onEditVehicle}
            >
              {hasVehicle ? "Change Vehicle" : "Add Vehicle"}
            </Button>
          )}
        </div>
        <SummaryField label="VRM" value={vehicle.registration} />
        <SummaryField label="Make & Model" value={vehicle.makeModel} />
        <SummaryField label="Colour" value={vehicle.colour} />
        <SummaryField label="Mileage" value={vehicle.mileage} />
      </section>

      <section>
        <div className="mb-1 flex items-center justify-between">
          <h3 className="text-[12px] font-semibold text-foreground">Finance</h3>
          {onEditFinance && (
            <Button
              variant="outline"
              size="sm"
              className="h-6 px-2 text-[11px]"
              onClick={onEditFinance}
            >
              Update Figures
            </Button>
          )}
        </div>
        <SummaryField label="Cash Price" value={finance.cashPrice} />
        <SummaryField label="Deposit" value={finance.deposit} />
        <SummaryField label="Part Ex Value" value={finance.partExchange ?? "—"} />
        <SummaryField label="Part Ex Settlement" value={finance.settlement ?? "—"} />
        <SummaryField label="Loan Amount" value={finance.loanAmount} />
        <SummaryField label="Term" value={finance.term} />
        <SummaryField label="Annual Mileage" value={finance.annualMileage} />
      </section>

      <section>
        <div className="mb-1 flex items-center justify-between">
          <h3 className="text-[12px] font-semibold text-foreground">Fees and Add-ons</h3>
          {onEditExtras && (
            <Button
              variant="outline"
              size="sm"
              className="h-6 px-2 text-[11px]"
              disabled={!hasVehicle}
              onClick={onEditExtras}
            >
              Update
            </Button>
          )}
        </div>
        {addOns.length === 0 ? (
          addOnsLoading && addOnsPending ? (
            <div className="flex items-center gap-2 py-2 text-[11px] text-muted-foreground">
              <Loader2 className="size-3.5 animate-spin" />
              Loading prices…
            </div>
          ) : (
            <p className="py-2 text-[11px] text-muted-foreground">
              {addOnsPending ? "Available after generating quotes." : "No fees or add-ons"}
            </p>
          )
        ) : (
          addOns.map((a) => (
            <div
              key={a.name}
              className="flex items-start justify-between gap-4 border-b border-border py-2 last:border-0"
            >
              <span className="text-[11px] text-muted-foreground">{a.name}</span>
              <span className="text-right">
                <span className="block text-[11px] text-foreground">{a.price}</span>
                <span className="text-[11px] text-muted-foreground">
                  {a.status === "In Finance" ? "Included in Finance" : "Cash"}
                </span>
                {a.soldBy && (
                  <span className="block text-[11px] text-muted-foreground">Sold by {a.soldBy}</span>
                )}
              </span>
            </div>
          ))
        )}
      </section>
    </div>
  );
}

// Quotes table

const GBP = formatUnderwritingQuoteAmount;

const PCT = (n: number | null | undefined) =>
  n != null ? `${n.toFixed(1)}%` : "—";

function FinanceTypeBadge({ type }: { type: number | null | undefined }) {
  if (!type) return null;
  const label = type === FinanceType.PCP ? "PCP" : "HP";
  const cls = "border-slate-300 bg-slate-50 text-slate-700";
  return (
    <span className={cn("inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium", cls)}>
      {label}
    </span>
  );
}

/** Unique key for a quote row — mirrors Flex's key which includes term to handle multi-term results. */
const quoteKey = (q: Pick<MonthlyPrice, "lenderId" | "financeType" | "term">) =>
  `${q.lenderId}-${q.financeType}-${q.term ?? 0}`;


/** Statuses that cannot be proposed to a lender. */
const NON_PROPOSABLE_STATUSES = ["Failed Send Rules", "Failed Rules", "Declined", "Expired", "Error"];

/** Ordering of the "Other Decisions" section, mirroring the pricing UI's STATUS_PRIORITY. */
const STATUS_PRIORITY: Record<string, number> = {
  Accepted: 1,
  "Provisional Acceptance": 2,
  "Conditionally Accepted": 2,
  "Failed Send Rules": 3,
  "Failed Rules": 3,
  Error: 3,
  Declined: 4,
  "Manual Processing": 5,
  Processing: 5,
  "More Info": 5,
  Expired: 5,
};

/** Buckets a quote is sorted into within the quotes table. */
type QuoteGroup = "main" | "processing" | "other" | "failed" | "declined";

const SUCCESS_STATUSES = new Set(["Accepted", "Provisional Acceptance", "Conditionally Accepted"]);
const PROCESSING_STATUSES = new Set(["Processing", "Manual Processing"]);
const FAILED_STATUSES = new Set(["Failed Rules", "Failed Send Rules", "Error"]);

const isProposableStatus = (s: string) => !NON_PROPOSABLE_STATUSES.includes(s);

/** A quote that can be proposed to a lender (not failed, and a proposable status). */
const isProposableQuote = (q: MonthlyPrice) => !q.failed && isProposableStatus(getDisplayStatus(q));

/** Sort for the Failed Rules / Declined sections: proposable first, then by status priority, then by metric. */
function compareOtherDecisions(a: MonthlyPrice, b: MonthlyPrice, sortBy: QuoteSortBy): number {
  const proposableA = isProposableQuote(a);
  const proposableB = isProposableQuote(b);
  if (proposableA !== proposableB) return proposableA ? -1 : 1;
  const priorityA = STATUS_PRIORITY[getDisplayStatus(a)] ?? 99;
  const priorityB = STATUS_PRIORITY[getDisplayStatus(b)] ?? 99;
  if (priorityA !== priorityB) return priorityA - priorityB;
  return compareQuotes(a, b, sortBy);
}

/** A card whose body can be collapsed by clicking the header (used for Failed Rules / Declined). */
function CollapsibleCard({
  title,
  count,
  defaultOpen = true,
  children,
}: {
  title: string;
  count?: number;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border border-border bg-card shadow-sm">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "flex w-full items-center gap-2 px-4 py-3 text-left",
          open && "border-b border-border"
        )}
      >
        <h2 className="text-[12px] font-semibold text-foreground">{title}</h2>
        {count != null && <span className="text-[11px] text-muted-foreground">({count})</span>}
        <ChevronDown
          className={cn("size-4 text-muted-foreground transition-transform", !open && "-rotate-90")}
        />
      </button>
      {open && children}
    </div>
  );
}

/** Lender / Product / Reason table for the Failed Rules and Declined sections. */
function DecisionTable({ rows, variant }: { rows: MonthlyPrice[]; variant: "failed" | "declined" }) {
  const th = "px-3 py-2 text-left text-[12px] font-semibold text-muted-foreground whitespace-nowrap";
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-border bg-muted/40">
            <th className={th}>Lender</th>
            <th className={th}>Product</th>
            <th className={th}>Reason</th>
          </tr>
        </thead>
        <tbody className="text-[12px]">
          {rows.map((q) => {
            const reason = q.failReasons?.length
              ? q.failReasons.map((r) => r.message).join("; ")
              : getDisplayStatus(q);
            return (
              <tr key={quoteKey(q)} className="border-b border-border last:border-0">
                <td className="px-3 py-2.5 font-medium text-foreground">
                  <span className="block max-w-[150px] truncate" title={q.lenderName ?? undefined}>
                    {q.lenderName ?? "—"}
                  </span>
                </td>
                <td className="px-3 py-2.5">
                  <FinanceTypeBadge type={q.financeType} />
                </td>
                <td className="px-3 py-2.5">
                  <span className="inline-flex items-start gap-2 text-muted-foreground">
                    {variant === "failed" ? (
                      <AlertCircle className="mt-px size-4 shrink-0 text-amber-500" />
                    ) : (
                      <XCircle className="mt-px size-4 shrink-0 text-rose-500" />
                    )}
                    <span>{reason}</span>
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function QuotesTable({
  quotes,
  requestedLender,
  bestLender,
  requestedDeposit,
  onPropose,
  onDetails,
  proposingQuoteKey,
  canPropose,
  headerAction,
  errorMessage,
}: {
  quotes: MonthlyPrice[];
  requestedLender?: QuoteLenderRef | null;
  bestLender?: QuoteLenderRef | null;
  requestedDeposit: number;
  onPropose: (quote: MonthlyPrice) => void;
  onDetails: (quote: MonthlyPrice) => void;
  proposingQuoteKey: string | null;
  canPropose: boolean;
  headerAction?: ReactNode;
  errorMessage?: string | null;
}) {
  const [sortBy, setSortBy] = useState<QuoteSortBy>("Monthly Payment");

  const groupOf = (q: MonthlyPrice): QuoteGroup => {
    const status = getDisplayStatus(q);
    const depositExceeds = (q.deposit ?? 0) > requestedDeposit;
    if (status === "Declined") return "declined";
    if (q.failed || FAILED_STATUSES.has(status)) return "failed";
    // Match Flex: deposit within limit and lender accepted
    if (!depositExceeds && SUCCESS_STATUSES.has(status)) return "main";
    if (PROCESSING_STATUSES.has(status)) return "processing";
    return "other";
  };

  const byGroup: Record<QuoteGroup, MonthlyPrice[]> = {
    main: [],
    processing: [],
    other: [],
    failed: [],
    declined: [],
  };
  for (const q of quotes) byGroup[groupOf(q)].push(q);

  const mainQuotes = byGroup.main.sort((a, b) => compareQuotes(a, b, sortBy));
  // Processing and other approvals render below the main quotes, in that order.
  const belowMainQuotes = [
    ...byGroup.processing.sort((a, b) => compareQuotes(a, b, sortBy)),
    ...byGroup.other.sort((a, b) => compareQuotes(a, b, sortBy)),
  ];
  const failedRules = byGroup.failed.sort((a, b) => compareOtherDecisions(a, b, sortBy));
  const declined = byGroup.declined.sort((a, b) => compareOtherDecisions(a, b, sortBy));

  // The snapshot may store financeType as a string ("HP"/"PCP") while quotes use
  // the numeric FinanceType enum. Normalise both to the enum before comparing.
  const normaliseFinanceType = (ft: FinanceType | string | null | undefined): FinanceType | null => {
    if (ft == null) return null;
    if (typeof ft === "number") return ft;
    const upper = ft.toUpperCase();
    if (upper === "HP") return FinanceType.HP;
    if (upper === "PCP") return FinanceType.PCP;
    return null;
  };

  const matchesRef = (q: MonthlyPrice, ref?: QuoteLenderRef | null) =>
    ref != null &&
    q.lenderId === ref.lenderId &&
    normaliseFinanceType(q.financeType) === normaliseFinanceType(ref.financeType);

  const th = "px-2 py-2 text-left text-[12px] font-semibold text-muted-foreground whitespace-nowrap";
  const SortHeader = ({ label, col }: { label: string; col: QuoteSortBy }) => (
    <th className={cn(th, "cursor-pointer select-none")} onClick={() => setSortBy(col)}>
      <span className="inline-flex items-center gap-1">
        {label}
        {sortBy === col && <ChevronUp className="size-3" />}
      </span>
    </th>
  );

  const renderRow = (q: MonthlyPrice, extraCls?: string) => {
    const isRequested = matchesRef(q, requestedLender);
    const isBest = matchesRef(q, bestLender);
    const depositExceeds = (q.deposit ?? 0) > requestedDeposit;
    const isProcessing = isQuoteProcessing(q);
    const isProvisionalAcceptance = isQuoteProvisionalAcceptance(q);
    const pxEquity = (q.pxValue ?? 0) - (q.pxSettlement ?? 0);
    const rowCls = cn(
      "border-b border-border text-[11px] transition-colors",
      isRequested && "bg-amber-50",
      isBest && !isRequested && "bg-emerald-50",
      isProcessing && !isBest && !isRequested && "bg-slate-100",
      extraCls,
    );
    const borderCls = isBest ? "border-l-4 border-l-emerald-500" : isRequested ? "border-l-4 border-l-amber-400" : "";

    return (
      <tr key={quoteKey(q)} className={rowCls}>
        <td className={cn("px-2 py-2 font-medium text-foreground", borderCls)}>
          <div className="flex items-center gap-1.5">
            <span className="max-w-[150px] truncate" title={q.lenderName ?? undefined}>
              {q.lenderName ?? "—"}
            </span>
            {isProvisionalAcceptance && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
aria-label={`${isProvisionalAcceptance} status`}
                    className="inline-flex text-emerald-500 hover:text-emerald-600"
                  >
                    <CircleDashedCheck className="size-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>Lender status is {isProvisionalAcceptance}</TooltipContent>
              </Tooltip>
            )}
            {q.infoMessages && q.infoMessages.length > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label="Lender note"
                    className="inline-flex text-amber-500 hover:text-amber-600"
                  >
                    <Info className="size-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-[240px] whitespace-normal">
                  {q.infoMessages.join(" ")}
                </TooltipContent>
              </Tooltip>
            )}
            {isProcessing && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label="Processing status"
                    className="inline-flex text-amber-500 hover:text-amber-600"
                  >
                    <Info className="size-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>Lender status is {isProcessing}</TooltipContent>
              </Tooltip>
            )}
            {isBest && (
              <span className="inline-flex items-center whitespace-nowrap rounded px-2 py-0.5 text-[11px] font-semibold border-emerald-200 bg-emerald-100 text-slate-800">
                Panel #1
              </span>
            )}
            {isRequested && (
              <span className="inline-flex items-center whitespace-nowrap rounded px-2 py-0.5 text-[11px] font-semibold border-amber-200 bg-amber-100 text-slate-500">
                Requested
              </span>
            )}
          </div>
        </td>
        <td className="px-2 py-2"><FinanceTypeBadge type={q.financeType} /></td>
        <td className="px-2 py-2 text-muted-foreground whitespace-nowrap">{q.term} mo</td>
        <td className="px-2 py-2 font-semibold text-foreground whitespace-nowrap">{GBP(q.monthlyPayment)}</td>
        <td className="px-2 py-2 text-muted-foreground whitespace-nowrap">{PCT(q.effectiveRate)}</td>
        <td className="px-2 py-2 text-muted-foreground whitespace-nowrap">{GBP(q.vehiclePrice)}</td>
        <td className="px-2 py-2 whitespace-nowrap">
          <span
            className={cn(
              "inline-flex items-center gap-1",
              depositExceeds ? "font-medium text-amber-600" : "text-muted-foreground"
            )}
            title={depositExceeds ? "Required deposit exceeds the customer's deposit" : undefined}
          >
            {depositExceeds && <AlertCircle className="size-3.5" />}
            {GBP(q.deposit)}
          </span>
        </td>
        <td className="px-2 py-2 whitespace-nowrap">
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="cursor-help text-muted-foreground underline decoration-dotted underline-offset-2">
                {GBP(pxEquity)}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              PX Value {GBP(q.pxValue)} · Settlement {GBP(q.pxSettlement)}
            </TooltipContent>
          </Tooltip>
        </td>
        <td className="px-2 py-2 text-muted-foreground whitespace-nowrap">{GBP(q.balanceToFinance)}</td>
        <td className="px-2 py-2 text-muted-foreground whitespace-nowrap">{GBP(q.finalMonthlyPayment)}</td>
        <td className="px-2 py-2 text-muted-foreground whitespace-nowrap">{GBP(q.commissionAmount)}</td>
        <td className="px-2 py-2 whitespace-nowrap">
          <div className="flex items-center gap-1.5">
            <Button
              size="sm"
              variant="ghost"
              className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
              onClick={() => onDetails(q)}
              aria-label="Quote details"
              title="View full breakdown"
            >
              <List className="size-4" />
            </Button>
            <Button
              size="sm"
              className="h-7 bg-white border border-slate-300 hover:bg-slate-300 text-[11px] text-slate-700 px-3"
              onClick={() => onPropose(q)}
              disabled={proposingQuoteKey != null || !canPropose}
              title={!canPropose ? "Claim this request to propose to a lender" : undefined}
            >
              {proposingQuoteKey === quoteKey(q) ? (
                <>
                  <Loader2 className="size-3 animate-spin mr-1" />
                  Proposing…
                </>
              ) : (
                "Propose"
              )}
            </Button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <SectionCard title="Quotes" action={headerAction}>
        {errorMessage && (
          <p className="border-b border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-600">
            {errorMessage}
          </p>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                <th className={th}>Lender</th>
                <th className={th}>Product</th>
                <th className={th}>Term</th>
                <SortHeader label="Monthly" col="Monthly Payment" />
                <SortHeader label="APR" col="APR" />
                <th className={th}>Cash Price</th>
                <th className={th}>Deposit</th>
                <th className={th}>PX Equity</th>
                <th className={th}>Loan</th>
                <th className={th}>Final Monthly</th>
                <th className={th}>Commission</th>
                <th className={th + " !text-center"}>Action</th>
              </tr>
            </thead>
            <tbody className="text-[12px]">
              {mainQuotes.length === 0 && belowMainQuotes.length === 0 ? (
                <tr>
                  <td colSpan={12} className="px-3 py-6 text-center text-[12px] text-muted-foreground">
                    No proposable quotes.
                  </td>
                </tr>
              ) : (
                <>
                  {mainQuotes.map((q) => renderRow(q))}
                  {belowMainQuotes.map((q) => renderRow(q, "opacity-80"))}
                </>
              )}
            </tbody>
          </table>
        </div>
      </SectionCard>

      {failedRules.length > 0 && (
        <CollapsibleCard title="Failed Rules" count={failedRules.length}>
          <DecisionTable rows={failedRules} variant="failed" />
        </CollapsibleCard>
      )}

      {declined.length > 0 && (
        <CollapsibleCard title="Declined" count={declined.length}>
          <DecisionTable rows={declined} variant="declined" />
        </CollapsibleCard>
      )}
    </div>
  );
}

/**
 * Shared application detail view. `editable` (queue apps) enables the vehicle /
 * finance / fees edit modals; open-request apps render the same view read-only.
 */
export function UnderwritingApplicationDetail({ editable = false }: { editable?: boolean }) {
  const { applicationId: applicationIdParam } = useParams<{ applicationId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, workerOptions } = useAppData();

  const navState = location.state as { activity?: UnderwritingActivity; justClaimed?: boolean; origin?: UnderwritingNavOrigin } | null;
  const activity = navState?.activity;
  const applicationId = applicationIdParam ? Number(applicationIdParam) : null;
  const historyResult = useApi(
    (signal) => underwritingService.getHistory(applicationId!, signal),
    [applicationId],
    { skip: applicationId == null }
  );

  // Tracks which right-column tabs have been opened at least once for this application, so
  // their content (and fetched data) stays mounted across tab switches instead of refetching
  // every time — reset only when the user navigates to a different application.
  const [visitedTabs, setVisitedTabs] = useState<Set<string>>(() => new Set(["summary"]));
  useEffect(() => {
    setVisitedTabs(new Set(["summary"]));
  }, [applicationId]);

  // Where the user came from, so the breadcrumb + back navigation return to the origin page.
  const origin = useMemo<UnderwritingNavOrigin>(
    () => navState?.origin ?? { path: "/underwriting-queue", label: "Underwriting Queue" },
    [navState?.origin]
  );

  // Claim handler — assigns the current user to this request.
  // When the page was opened right after claiming from the dashboard, the passed-in
  // activity data is still stale, so trust the `justClaimed` flag to mark it as ours.
  const [lockedBy, setLockedBy] = useState<string | null>(
    navState?.justClaimed ? currentUser?.name ?? null : activity?.claimedBy ?? null
  );
  const handleClaim = useCallback(async () => {
    if (!activity || !currentUser?.name) return;
    await underwritingService.claimRequest(activity, currentUser.name);
    setLockedBy(currentUser.name);
  }, [activity, currentUser]);

  const handleUnclaim = useCallback(async () => {
    if (!activity || !currentUser?.name) return;
    await underwritingService.unclaimRequest(activity, currentUser.name);
    setLockedBy(null);
  }, [activity, currentUser]);

  // Only the user who claimed the request may action it
  const isClaimedByMe = lockedBy !== null && lockedBy === currentUser?.name;

  // Action handler — submits the underwriter's decision by creating an action
  const snapshotResult = useApi(
    (signal) => quoteSnapshotService.listByApp(applicationId!, signal),
    [applicationId],
    { skip: applicationId == null }
  );

  const snapshot = snapshotResult.data?.[0]?.snapshot ?? null;

  // A vehicle selected via VRM search overrides the snapshot's vehicle in the Summary tab.
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleDetailModel | null>(null);
  const [vehicleModalOpen, setVehicleModalOpen] = useState(false);

  // Finance figures and fees/add-ons can be overridden from their edit modals.
  const [selectedFinance, setSelectedFinance] = useState<FinanceFiguresInput | null>(null);
  const [financeModalOpen, setFinanceModalOpen] = useState(false);
  const [selectedAddOns, setSelectedAddOns] = useState<AddOn[] | null>(null);
  // Calculator-ready overrides from the fees modal (feed the next quote generation).
  const [selectedIncludedExtras, setSelectedIncludedExtras] = useState<QuoteSnapshotPayload["includedExtras"]>(null);
  const [selectedAdminFee, setSelectedAdminFee] = useState<number | null>(null);
  const [extrasModalOpen, setExtrasModalOpen] = useState(false);

  const hasVehicle = selectedVehicle != null || snapshot?.vehicles?.[0] != null;

  // The application's current vehicle as a VehicleDetailModel, so the modal can
  // pre-load it and let the agent update mileage without re-searching.
  const initialVehicle = useMemo<VehicleDetailModel | null>(() => {
    if (selectedVehicle) return selectedVehicle;
    const v = snapshot?.vehicles?.[0];
    if (!v) return null;
    return {
      vehicleId: v.vehicleId ?? 0,
      regNumber: v.vrm ?? "",
      make: v.make ?? "",
      model: v.model ?? "",
      trim: v.trim,
      colour: v.colour ?? "",
      mileage: v.mileage ?? null,
      price: v.price ?? 0,
      year: v.year ?? null,
      registrationDate: v.registrationDate ? String(v.registrationDate).slice(0, 10) : null,
      vehicleType: v.vehicleType ?? null,
      dealer: null,
    };
  }, [selectedVehicle, snapshot]);
  
  const liveProposalsResult = useApi(
    (signal) => underwritingService.getAvailableProposals(applicationId!, signal),
    [applicationId],
    { skip: applicationId == null }
  );

  // Prefer live proposals; fall back to snapshot's stored proposals if the fetch failed.
  const usingSnapshotProposals = liveProposalsResult.error != null || (liveProposalsResult.data != null && liveProposalsResult.data.length === 0 && (snapshot?.proposals?.length ?? 0) > 0);
  const effectiveProposals = useMemo(
    () =>
      liveProposalsResult.data && liveProposalsResult.data.length > 0
        ? mapProposals(liveProposalsResult.data)
        : mapProposals(snapshot?.proposals),
    [liveProposalsResult.data, snapshot]
  );

  const vehicleDetails = useMemo(
    () =>
      selectedVehicle
        ? vehicleDetailsFromModel(selectedVehicle)
        : vehicleDetailsFromSnapshot(snapshot?.vehicles?.[0]),
    [selectedVehicle, snapshot]
  );
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [quotesError, setQuotesError] = useState<string | null>(null);
  const [quotes, setQuotes] = useState<MonthlyPrice[] | null>(null);
  const [proposalsRefreshing, setProposalsRefreshing] = useState(false);
  const [proposingQuoteKey, setProposingQuoteKey] = useState<string | null>(null);
  const [detailsQuote, setDetailsQuote] = useState<MonthlyPrice | null>(null);

  // Clearing quotes brings back the "Generate Quotes" button after an edit.
  const clearQuotes = useCallback(() => {
    setQuotes(null);
    setQuotesError(null);
  }, []);

  const effectiveDealer = useMemo<Dealer | undefined>(() => {
    const dealer = snapshot?.dealer;
    if (!dealer || selectedAdminFee == null) return dealer;
    return {
      ...dealer,
      adminFee: {
        ...(dealer.adminFee ?? {}),
        amount: selectedAdminFee,
        includedInFinance: dealer.adminFee?.includedInFinance ?? true,
      },
    } as Dealer;
  }, [snapshot, selectedAdminFee]);

  const calculateDealer = useMemo(
    () => (snapshot ? mapSnapshotDealer(effectiveDealer ?? snapshot.dealer) : undefined),
    [snapshot, effectiveDealer]
  );

  // Effective calculate() inputs — snapshot values overridden by the edit modals.
  const effectiveVehicles = useMemo<Vehicle[]>(() => {
    const base = snapshot?.vehicles ?? [];
    if (!snapshot || base.length === 0) return base;
    const [first, ...rest] = base;
    const merged: Vehicle = { ...first };
    if (selectedVehicle) {
      merged.make = selectedVehicle.make;
      merged.model = selectedVehicle.model;
      if (selectedVehicle.trim) merged.trim = selectedVehicle.trim;
      if (selectedVehicle.colour) merged.colour = selectedVehicle.colour;
      if (selectedVehicle.mileage != null) merged.mileage = selectedVehicle.mileage;
      if (selectedVehicle.regNumber) merged.vrm = selectedVehicle.regNumber;
      merged.price = selectedVehicle.price;
      if (selectedVehicle.registrationDate) merged.registrationDate = new Date(selectedVehicle.registrationDate);
      if (selectedVehicle.vehicleType != null) merged.vehicleType = selectedVehicle.vehicleType as Vehicle["vehicleType"];
    }
    if (selectedFinance) merged.price = selectedFinance.cashPrice;
    const dealer = calculateDealer ?? mapSnapshotDealer(snapshot.dealer);
    return [
      mapSnapshotVehicle(merged, dealer),
      ...rest.map((vehicle) => mapSnapshotVehicle(vehicle, dealer)),
    ];
  }, [snapshot, selectedVehicle, selectedFinance, calculateDealer]);

  const effectiveIncludedExtras = selectedIncludedExtras ?? snapshot?.includedExtras ?? null;

  const [confirmProposeQuote, setConfirmProposeQuote] = useState<MonthlyPrice | null>(null);
  const baseFinanceFigures = useMemo(
    () => {
      const figures = financeFiguresFromSnapshot(
        snapshot?.vehicles?.[0],
        snapshot?.deposit ?? 0,
        snapshot?.pxValue ?? 0,
        snapshot?.pxSettlement ?? 0,
        snapshot?.annualMileage ?? 0,
        snapshot?.maxTerm ?? 0,
        snapshot?.dealer
      );
      // The snapshot can't price finance-stance extras, so the computed loan above
      // is an approximation. Once quotes are generated their balanceToFinance is
      // authoritative — use it (with the same GBP formatting) so this matches the
      // "Loan" column in the quotes list exactly.
      const quoteLoan = quotes?.find((q) => q.balanceToFinance != null && q.balanceToFinance > 0)?.balanceToFinance;
      if (quoteLoan != null) {
        figures.loanAmount = GBP(quoteLoan);
      }
      return figures;
    },
    [snapshot, quotes]
  );

  const financeFigures =
    selectedFinance && (selectedFinance.loanAmount > 0 || quotes == null)
      ? financeFiguresFromInput(selectedFinance)
      : baseFinanceFigures;

  // Numeric seed for the Change Finance Figures modal.
  const financeInput = useMemo<FinanceFiguresInput>(() => {
    if (selectedFinance && (selectedFinance.loanAmount > 0 || quotes == null)) return selectedFinance;
    const price = snapshot?.vehicles?.[0]?.price ?? 0;
    const quoteLoan = quotes?.find((q) => q.balanceToFinance != null && q.balanceToFinance > 0)?.balanceToFinance;
    const computedLoan = price - (snapshot?.deposit ?? 0) - (snapshot?.pxValue ?? 0) + (snapshot?.pxSettlement ?? 0);
    return {
      cashPrice: price,
      deposit: snapshot?.deposit ?? 0,
      partExchange: snapshot?.pxValue ?? 0,
      settlement: snapshot?.pxSettlement ?? 0,
      loanAmount: Math.round(quoteLoan ?? (computedLoan > 0 ? computedLoan : 0)),
      term: snapshot?.maxTerm ?? 0,
      annualMileage: snapshot?.annualMileage ?? 0,
    };
  }, [selectedFinance, snapshot, quotes]);

  // Add-ons come from the snapshot's included extras; names/prices are enriched
  // from the generated quotes. The dealer admin fee is surfaced as an add-on
  // (flagged "In Finance" or "Not Included" so the Summary can label it Cash).
  // Falls back to any activity-provided add-ons.
  const baseAddOns = useMemo(() => {
    const result: AddOn[] = [];
    const adminFee = snapshot?.dealer?.adminFee;
    if ((adminFee?.amount ?? 0) > 0) {
      result.push({
        name: "Admin Fee",
        price: formatCurrency(adminFee!.amount ?? 0),
        status: adminFee!.includedInFinance ? "In Finance" : "Not Included",
      });
    }
    result.push(...addOnsFromSnapshot(snapshot?.includedExtras, quotes));
    return result.length ? result : activity?.addOns ?? [];
  }, [snapshot, quotes, activity]);

  // A change made via the Fees and Add-ons modal overrides the snapshot-derived list.
  const addOns = selectedAddOns ?? baseAddOns;

  // Current admin fee amount seeds the fees modal.
  const currentAdminFee = useMemo(() => {
    const override = selectedAddOns?.find((a) => a.name === "Admin Fee");
    if (override) return Number(override.price.replace(/[^0-9.]/g, "")) || 0;
    return snapshot?.dealer?.adminFee?.amount ?? 0;
  }, [selectedAddOns, snapshot]);

  // Extras already on the quote (excluding the admin fee) preselect the fees modal.
  const currentExtras = useMemo(
    () =>
      addOns
        .filter((a) => a.name !== "Admin Fee")
        .map((a) => ({
          description: a.name,
          payment: (a.status === "In Finance" ? "finance" : "cash") as "finance" | "cash",
          soldByAgentId: a.soldByAgentId,
        })),
    [addOns]
  );

  // Vehicle attributes used to query available extras (from the searched vehicle or snapshot).
  const extrasVehicleParams = useMemo<VehicleExtraFilterParams | null>(() => {
    const price = selectedFinance?.cashPrice;
    if (selectedVehicle) {
      return {
        make: selectedVehicle.make,
        model: selectedVehicle.model,
        mileage: selectedVehicle.mileage ?? 0,
        price: price ?? selectedVehicle.price,
        registrationDate: selectedVehicle.registrationDate ?? undefined,
        vehicleType: selectedVehicle.vehicleType ?? undefined,
        engine: selectedVehicle.engine ?? undefined,
      };
    }
    const v = snapshot?.vehicles?.[0];
    if (!v) return null;
    return {
      make: v.make,
      model: v.model,
      mileage: v.mileage ?? 0,
      price: price ?? v.price,
      registrationDate: v.registrationDate ? new Date(v.registrationDate).toISOString().slice(0, 10) : undefined,
      vehicleType: v.vehicleType ?? undefined,
    };
  }, [selectedVehicle, snapshot, selectedFinance]);

  const handleExtrasConfirm = useCallback(
    ({ adminFee, extras }: { adminFee: number; extras: SelectedExtra[] }) => {
      const next: AddOn[] = [];
      if (adminFee > 0) {
        next.push({ name: "Admin Fee", price: formatCurrency(adminFee), status: "In Finance" });
      }
      for (const { option, payment, soldByAgentId, soldByName } of extras) {
        next.push({
          name: option.description,
          price: formatCurrency(option.price),
          status: payment === "finance" ? "In Finance" : "Not Included",
          soldBy: soldByName || undefined,
          soldByAgentId: soldByAgentId ?? undefined,
        });
      }
      setSelectedAddOns(next);
      // Build the calculator-ready extras so the next generation prices them.
      setSelectedIncludedExtras(
        extras.map(({ option, payment, soldByAgentId }) => ({
          productId: option.vehicleExtraOptionId,
          stance:
            payment === "finance"
              ? AncillaryProductLenderStanceType.Finance
              : AncillaryProductLenderStanceType.Cash,
          vehicleExtraTypeId: option.vehicleExtraTypeId,
          vehicleExtraTypeName: option.vehicleExtraTypeName,
          soldByAgentId: soldByAgentId ?? null,
        }))
      );
      setSelectedAdminFee(adminFee);
      clearQuotes();
    },
    [clearQuotes]
  );

  // A selected extra exists but its name/price can't be resolved until quotes run.
  const addOnsPending =
    selectedAddOns == null && (snapshot?.includedExtras?.length ?? 0) > 0 && baseAddOns.length === 0;

  // Action state — a proposal determines the (locked) decision when the popup opens
  const [actionOpen, setActionOpen] = useState(false);
  const [proposedInfo, setProposedInfo] = useState<{ lenderId: number; isRequested: boolean } | null>(null);
  const [hasActioned, setHasActioned] = useState(false);

  const handlePropose = useCallback(
    async (quote: MonthlyPrice) => {
      if (!snapshot || applicationId == null) return;
      setProposingQuoteKey(quoteKey(quote));
      const toastId = toast.loading(`Proposing to ${quote.lenderName || "lender"}…`);
      console.groupCollapsed(`[Propose] ${quote.lenderName} (${quoteKey(quote)})`);
      console.log("[Propose] quote", quote);
      console.log("[Propose] applicationId", applicationId);
      console.log("[Propose] vehicle", snapshot.vehicles?.[0]);
      console.log("[Propose] dealer", snapshot.dealer);
      console.log("[Propose] userId", currentUser?.userId ?? null);
      try {
        console.log("[Propose] mapping MonthlyPrice → QuoteRequest…");
        const quoteRequest = mapMonthlyPriceToQuoteRequest(
          quote,
          applicationId,
          snapshot.vehicles?.[0],
          snapshot.dealer,
          currentUser?.userId ?? null,
          snapshot.includedExtras
        );
        console.log("[Propose] quoteRequest", quoteRequest);

        console.log("[Propose] createQuote → POST…");
        const savedQuote = await proposalService.createQuote(quoteRequest);
        console.log("[Propose] savedQuote", savedQuote);

        console.log("[Propose] building loan proposal…");
        const loanProposal = buildLoanProposalRequest(savedQuote);
        console.log("[Propose] loanProposal", loanProposal);

        console.log("[Propose] sendLoanProposal → POST…");
        await proposalService.sendLoanProposal(loanProposal, savedQuote);
        console.log("[Propose] success");
        setProposedInfo({
          lenderId: quote.lenderId,
          isRequested:
            snapshot.requestedLender?.lenderId != null &&
            quote.lenderId === snapshot.requestedLender.lenderId,
        });
        toast.success(`Proposed to ${quote.lenderName || "lender"} successfully`, { id: toastId });
      } catch (err) {
        console.error("[Propose] failed", err);
        toast.error("Failed to propose quote to lender. Please try again.", { id: toastId });
      } finally {
        console.groupEnd();
        setProposingQuoteKey(null);
      }
    },
    [snapshot, applicationId, currentUser]
  );

  // Propose is confirmed via a modal first; only then is the quote actually proposed.
  const handleRequestPropose = useCallback((quote: MonthlyPrice) => {
    setDetailsQuote(null);
    setConfirmProposeQuote(quote);
  }, []);

  const handleConfirmPropose = useCallback(() => {
    const quote = confirmProposeQuote;
    setConfirmProposeQuote(null);
    if (quote) handlePropose(quote);
  }, [confirmProposeQuote, handlePropose]);

  // A proposal locks the decision: requested lender → Approved, otherwise → Alternative
  const lockedDecision: ActionDecision | undefined = proposedInfo
    ? proposedInfo.isRequested
      ? "Approved"
      : "Alternative"
    : undefined;

  const handleSubmitAction = useCallback(
    async (decision: ActionDecision, notes: string) => {
      if (!activity) return;
      if (activity.quoteUnderwritingRequestId == null) {
        toast.error("This request cannot be actioned.");
        throw new Error("Missing quoteUnderwritingRequestId");
      }
      const actionedLender = proposedInfo
        ? proposedInfo.lenderId
        : snapshot?.requestedLender?.lenderId ?? activity.requestedLender ?? activity.bestLender ?? 0;
      await underwritingService.createAction(activity.quoteUnderwritingRequestId, {
        quoteUnderwritingRequestId: activity.quoteUnderwritingRequestId,
        applicationId: activity.applicationId,
        handledBy: currentUser?.name ?? "",
        handledOn: toUkIsoString(),
        actionedLender,
        decision,
        notes: notes || null,
      });

      try {
        await underwritingService.addNote({
          applicationId: activity.applicationId,
          comment: `Propose request actioned by ${currentUser?.name ?? "Unknown"}. Status: ${decision}.${notes ? ` Notes: ${notes}` : ""}`,
          createdBy: currentUser?.name ?? "",
          isImportant: false,
          isPublic: false,
          isUserComment: true,
          userId: currentUser?.userId ?? 0,
        });
      } catch {
        toast.warning("Action submitted, but the note could not be added.");
      }

      setHasActioned(true);
      toast.success("Action submitted successfully");
      navigate(origin.path);
    },
    [activity, currentUser, proposedInfo, snapshot, navigate, origin]
  );

  // Auto-redirect to the dashboard if the application stays open too long without an action
  useEffect(() => {
    if (hasActioned) return;
    const timer = window.setTimeout(() => {
      toast.info("Application closed after 15 minutes of inactivity.");
      navigate(origin.path);
    }, INACTIVITY_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [hasActioned, navigate, origin]);

  // Warn before leaving after a proposal if no action has been recorded yet
  const shouldWarnOnLeave = proposedInfo != null && !hasActioned;

  useEffect(() => {
    if (!shouldWarnOnLeave) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [shouldWarnOnLeave]);

  const confirmLeave = useCallback(
    () => !shouldWarnOnLeave || window.confirm(LEAVE_WITHOUT_ACTION_MESSAGE),
    [shouldWarnOnLeave]
  );

  // Request overview — built from the request + snapshot, falling back to any API-provided overview
  const overview = useMemo<RequestOverview | null>(() => {
    if (!activity) return null;
    return {
      submittedBy: activity.agentName ?? activity.overview?.submittedBy ?? "",
      panelLenderName:
        lenderDisplayName(snapshot?.bestLender?.lenderId) ?? activity.overview?.panelLenderName ?? "",
      requestedLenderName:
        lenderDisplayName(snapshot?.requestedLender?.lenderId) ?? activity.overview?.requestedLenderName ?? "",
      subject: activity.subject ?? activity.overview?.subject ?? "",
      reason: activity.details ?? activity.overview?.reason ?? "",
    };
  }, [activity, snapshot]);

  const handleGenerateQuotes = useCallback(async () => {
    if (!snapshot) {
      setQuotesError("No quote snapshot available for this application.");
      return;
    }
    setQuotesLoading(true);
    setQuotesError(null);
    setQuotes(null);
    try {
      const result = await calculate(
        effectiveVehicles,
        effectiveProposals,
        mapSnapshotCustomer(snapshot.customer),
        calculateDealer ?? mapSnapshotDealer(snapshot.dealer),
        selectedFinance?.term ?? snapshot.maxTerm,
        selectedFinance?.deposit ?? snapshot.deposit,
        selectedFinance?.partExchange ?? snapshot.pxValue,
        selectedFinance?.settlement ?? snapshot.pxSettlement,
        selectedFinance?.annualMileage ?? snapshot.annualMileage,
        effectiveIncludedExtras,
        snapshot.source
      );
      setQuotes(result);
    } catch (err: unknown) {
      const message =
        typeof err === "object" && err !== null && "message" in err
          ? (err as { message: string }).message
          : "Failed to generate quotes. Please try again.";
      setQuotesError(message);
    } finally {
      setQuotesLoading(false);
    }
  }, [snapshot, effectiveProposals, effectiveVehicles, calculateDealer, effectiveIncludedExtras, selectedFinance]);

  // Auto-generate once both snapshot and live proposals have settled.
  const autoGeneratedRef = useRef(false);
  useEffect(() => {
    if (snapshot && !liveProposalsResult.loading && !autoGeneratedRef.current) {
      autoGeneratedRef.current = true;
      handleGenerateQuotes();
    }
  }, [snapshot, liveProposalsResult.loading, handleGenerateQuotes]);

  const waitingForProposalRefreshRef = useRef(false);
  const handleRefreshQuotesAndProposals = useCallback(() => {
    if (!snapshot || proposalsRefreshing || quotesLoading) return;
    waitingForProposalRefreshRef.current = false;
    setProposalsRefreshing(true);
    liveProposalsResult.refetch();
  }, [snapshot, proposalsRefreshing, quotesLoading, liveProposalsResult.refetch]);

  useEffect(() => {
    if (!proposalsRefreshing) return;
    if (liveProposalsResult.loading) {
      waitingForProposalRefreshRef.current = true;
      return;
    }
    if (!waitingForProposalRefreshRef.current) return;
    waitingForProposalRefreshRef.current = false;
    setProposalsRefreshing(false);
    if (liveProposalsResult.error) {
      setQuotesError(`Could not refresh proposals: ${liveProposalsResult.error}`);
      return;
    }
    handleGenerateQuotes();
  }, [proposalsRefreshing, liveProposalsResult.loading, liveProposalsResult.error, handleGenerateQuotes]);

  const handleDashboardClick = () => {
    if (!confirmLeave()) return;
    navigate(origin.path);
  };

  if (snapshotResult.loading) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground gap-2">
        <Loader2 className="size-5 animate-spin" />
        Loading application…
      </div>
    );
  }

  // No activity row means the user navigated directly (e.g. refreshed the page)
  if (!activity) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 text-muted-foreground">
        <p className="text-lg font-medium">Application not found</p>
        <p className="text-sm">Please navigate from the dashboard.</p>
        <Button variant="outline" onClick={handleDashboardClick}>
          ← Back to Dashboard
        </Button>
      </div>
    );
  }

  return (
    <div
      className="flex h-full flex-col overflow-hidden"
      style={{ "--foreground": "#334155" } as CSSProperties}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between border-b border-border px-6 py-3">
        <Breadcrumb customerName={activity.customerName ?? "—"} originLabel={origin.label} onDashboardClick={handleDashboardClick} />
        <div className="flex items-center gap-2">
          <HeaderClaimButton
            lockedBy={lockedBy}
            currentUserName={currentUser?.name ?? null}
            status={activity.status}
            claimedOn={activity.claimedOn ?? null}
            onClaim={handleClaim}
            onUnclaim={handleUnclaim}
          />
          <Button
            className="border-purple-600 border bg-purple-600 hover:bg-purple-700 text-white text-xs px-2.5 py-1.5 h-auto"
            onClick={() => setActionOpen(true)}
            disabled={!isClaimedByMe || !isRequestActionable(activity.status)}
            title={!isClaimedByMe ? "Claim this request to action it" : !isRequestActionable(activity.status) ? "This request cannot be actioned" : undefined}
          >
            Action
          </Button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-auto xl:overflow-hidden bg-muted/40">
        <div className="mx-auto grid w-full max-w-[1600px] grid-cols-1 gap-6 p-6 xl:grid-cols-[minmax(0,1fr)_620px] xl:h-full xl:min-h-0">
          {/* Left column */}
          <div className="flex min-w-0 flex-col gap-4 xl:min-h-0 xl:overflow-y-auto">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl font-bold text-foreground">{activity.customerName}</h1>
              <span className="text-sm text-muted-foreground">{formatTimeAgo(activity.createdOn)}</span>
            </div>

            {snapshotResult.error && (
              <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                Could not load quote snapshot: {snapshotResult.error}
              </div>
            )}

            {usingSnapshotProposals && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                Could not retrieve up-to-date lender proposals — using stored snapshot proposals instead. Quotes may not reflect current lender availability.
              </div>
            )}

            <SectionCard title="Overview">
              <OverviewContent activity={activity} overview={overview} lockedBy={lockedBy} />
            </SectionCard>

            {quotes ? (
              <QuotesTable
                quotes={quotes}
                requestedLender={snapshot?.requestedLender}
                bestLender={snapshot?.bestLender}
                requestedDeposit={snapshot?.deposit ?? 0}
                onPropose={handleRequestPropose}
                onDetails={setDetailsQuote}
                proposingQuoteKey={proposingQuoteKey}
                canPropose={isClaimedByMe}
                errorMessage={quotesError}
                headerAction={
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-2"
                    onClick={handleRefreshQuotesAndProposals}
                    disabled={!snapshot || quotesLoading || proposalsRefreshing || liveProposalsResult.loading}
                    title={!snapshot ? "No snapshot available for this application" : undefined}
                  >
                    {(proposalsRefreshing || liveProposalsResult.loading) ? (
                      <>
                        <Loader2 className="size-4 animate-spin" />
                        <p className="text-xs text-muted-foreground">Refreshing proposals…</p>
                      </>
                    ) : (
                      <>
                        <RefreshCw className="size-3.5 text-muted-foreground" />
                        <p className="text-xs text-muted-foreground">Refresh quotes/proposals</p>
                      </>
                    )}
                  </Button>
                }
              />
            ) : (
              <SectionCard title="Quotes">
                {(quotesLoading || (snapshot && liveProposalsResult.loading)) && (
                  <div className="flex items-center gap-2 px-4 py-6 text-sm text-muted-foreground">
                    <Loader2 className="size-4 animate-spin" />
                    {quotesLoading ? "Generating quotes…" : "Loading proposals…"}
                  </div>
                )}
                {quotesError && !quotesLoading && (
                  <p className="px-4 py-3 text-sm font-medium text-rose-600">{quotesError}</p>
                )}
                {!quotesLoading && !(snapshot && liveProposalsResult.loading) && (
                  <div className="flex flex-col items-center gap-3 py-10">
                    <p className="text-sm text-muted-foreground">
                      Quotes are generated at point of review to ensure figures are current.
                    </p>
                    <Button
                      className="bg-purple-600 hover:bg-purple-700 text-white px-8 gap-2"
                      onClick={handleGenerateQuotes}
                      disabled={!snapshot}
                      title={
                        !snapshot
                          ? "No snapshot available for this application"
                          : undefined
                      }
                    >
                      Generate Quotes
                    </Button>
                  </div>
                )}
              </SectionCard>
            )}
          </div>

          {/* Right column: tabs and persistent history */}
          <div className="grid min-w-0 overflow-hidden rounded-xl border border-border bg-card shadow-sm md:h-[600px] md:grid-cols-2 md:grid-rows-[minmax(0,1fr)] md:divide-x md:divide-border xl:h-full xl:min-h-0">
            <Tabs
              defaultValue="summary"
              variant="underline"
              className="flex h-[600px] min-w-0 flex-col gap-0 md:h-full"
              onValueChange={(value) =>
                setVisitedTabs((prev) => (prev.has(value) ? prev : new Set(prev).add(value)))
              }
            >
              <TabsList className="h-11 w-full shrink-0 justify-between gap-1 border-b border-border px-2">
                <TabsTrigger value="summary">Summary</TabsTrigger>
                <TabsTrigger value="notes">Notes</TabsTrigger>
                <TabsTrigger value="messages">Messages</TabsTrigger>
                <TabsTrigger value="transcripts">Transcripts</TabsTrigger>
              </TabsList>

              <TabsContent value="summary" className="min-h-0 flex-1 overflow-y-auto">
                <SummaryTab
                  vehicle={vehicleDetails}
                  finance={financeFigures}
                  addOns={addOns}
                  addOnsPending={addOnsPending}
                  addOnsLoading={quotesLoading}
                  hasVehicle={hasVehicle}
                  onEditVehicle={editable ? () => setVehicleModalOpen(true) : undefined}
                  onEditFinance={editable ? () => setFinanceModalOpen(true) : undefined}
                  onEditExtras={editable ? () => setExtrasModalOpen(true) : undefined}
                />
              </TabsContent>
              <TabsContent
                value="notes"
                forceMount={visitedTabs.has("notes") || undefined}
                className="h-0 min-h-0 flex-1 overflow-hidden data-[state=inactive]:hidden"
              >
                <NotesTab
                  applicationId={applicationId}
                  currentUserName={currentUser?.name ?? null}
                  currentUserId={currentUser?.userId ?? null}
                  onNoteAdded={historyResult.refetch}
                />
              </TabsContent>
              <TabsContent
                value="messages"
                forceMount={visitedTabs.has("messages") || undefined}
                className="h-0 min-h-0 flex-1 overflow-hidden data-[state=inactive]:hidden"
              >
                <MessagesTab applicationId={applicationId} />
              </TabsContent>
              <TabsContent
                value="transcripts"
                forceMount={visitedTabs.has("transcripts") || undefined}
                className="h-0 min-h-0 flex-1 overflow-hidden data-[state=inactive]:hidden"
              >
                <TranscriptsTab applicationId={applicationId} />
              </TabsContent>
            </Tabs>
            <section aria-label="History" className="flex h-[300px] min-w-0 flex-col border-t border-border md:h-full md:min-h-0 md:border-t-0">
              <div className="flex h-11 shrink-0 items-center justify-between border-b border-border px-4">
                <h2 className="text-sm font-medium text-foreground">History</h2>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-7 w-7 p-0"
                  onClick={historyResult.refetch}
                  disabled={historyResult.loading}
                  aria-label="Refresh history"
                  title="Refresh history"
                >
                  <RefreshCw className={cn("size-3.5", historyResult.loading && "animate-spin")} />
                </Button>
              </div>
              <div className="min-h-0 flex-1">
                <HistoryTab historyResult={historyResult} />
              </div>
            </section>
          </div>
        </div>
      </div>

      <ActionRequestDialog
        open={actionOpen}
        onOpenChange={setActionOpen}
        applicationId={activity.applicationId}
        lockedDecision={lockedDecision}
        onSubmit={handleSubmitAction}
      />

      <QuoteDetailsDialog
        open={detailsQuote != null}
        onOpenChange={(o) => {
          if (!o) setDetailsQuote(null);
        }}
        quote={detailsQuote}
        onPropose={handleRequestPropose}
        proposing={detailsQuote != null && proposingQuoteKey === quoteKey(detailsQuote)}
        canPropose={isClaimedByMe}
      />

      {editable && (
        <>
          {applicationId != null && (
            <AddVehicleModal
              open={vehicleModalOpen}
              onOpenChange={setVehicleModalOpen}
              applicationId={applicationId}
              mode={hasVehicle ? "change" : "add"}
              initialVehicle={initialVehicle}
              onConfirm={(v) => {
                setSelectedVehicle(v);
                clearQuotes();
              }}
            />
          )}

          <ChangeFinanceModal
            open={financeModalOpen}
            onOpenChange={setFinanceModalOpen}
            initial={financeInput}
            onConfirm={(f) => {
              setSelectedFinance(f);
              clearQuotes();
            }}
          />

          <ChangeExtrasModal
            open={extrasModalOpen}
            onOpenChange={setExtrasModalOpen}
            vehicle={extrasVehicleParams}
            initialAdminFee={currentAdminFee}
            agentOptions={workerOptions}
            initialExtras={currentExtras}
            onConfirm={handleExtrasConfirm}
          />
        </>
      )}

      <Dialog
        open={confirmProposeQuote != null}
        onOpenChange={(o) => {
          if (!o) setConfirmProposeQuote(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Propose to lender</DialogTitle>
            <DialogDescription>
              Do you want to propose to {confirmProposeQuote?.lenderName || "this lender"}?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-3 sm:gap-3">
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button
              className="bg-purple-600 hover:bg-purple-700 text-white"
              onClick={handleConfirmPropose}
            >
              Propose
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Detail page for open-request applications (dashboard / all requests) — read-only summary. */
export function UnderwritingApplicationDetailPage() {
  return <UnderwritingApplicationDetail editable={false} />;
}
